import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  createContact,
  createDeal,
  createLead,
  fetchContacts,
  type Contact,
} from '../api/resources';
import { defaultStages, resolveStageId } from '../constants/pipelineStages';
import { normalizeHeader, parseCsv, type ParsedCsv } from '../utils/csv';

type ImportType = 'contacts' | 'leads' | 'deals';

type ImportResult = {
  success: number;
  failed: number;
  createdContacts: number;
  warnings: string[];
  errors: string[];
};

const typeOptions: { value: ImportType; label: string }[] = [
  { value: 'contacts', label: 'Contacts' },
  { value: 'leads', label: 'Leads' },
  { value: 'deals', label: 'Deals' },
];

const columnGuides: Record<ImportType, string[]> = {
  contacts: ['firstName', 'lastName', 'email', 'phone', 'company', 'tags'],
  leads: [
    'contactEmail',
    'status',
    'source',
    'score',
    'notes',
    '(optional) firstName/lastName to auto-create missing contacts',
  ],
  deals: [
    'name',
    'contactEmail',
    'stage (New, Contacted, Qualified, Proposal, Closed Won)',
    'value',
    'probability',
    'closeDate (YYYY-MM-DD)',
  ],
};

const importDescriptions: Record<ImportType, string> = {
  contacts: 'Upload a CSV of people to add as contacts. We will create a new record for every row.',
  leads: 'Import leads tied to contacts. We match existing contacts by email and can create them automatically when needed.',
  deals: 'Bulk-create deals linked to existing contacts. Provide contact email and deal details for each row.',
};

function initResult(): ImportResult {
  return { success: 0, failed: 0, createdContacts: 0, warnings: [], errors: [] };
}

function buildIndex(headers: string[]): Map<string, number> {
  const map = new Map<string, number>();
  headers.forEach((header, idx) => {
    const normalized = normalizeHeader(header);
    if (!map.has(normalized)) {
      map.set(normalized, idx);
    }
  });
  return map;
}

function getField(row: string[], index: Map<string, number>, keys: string[]): string {
  for (const key of keys) {
    const normalized = normalizeHeader(key);
    const idx = index.get(normalized);
    if (idx !== undefined) {
      return (row[idx] ?? '').trim();
    }
  }
  return '';
}

function parseNumber(value: string): number | undefined {
  if (!value) return undefined;
  const num = Number(value);
  if (Number.isNaN(num)) return undefined;
  return num;
}

function parseDateToSeconds(value: string): number | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  const time = date.getTime();
  if (Number.isNaN(time)) return undefined;
  return Math.floor(time / 1000);
}

const expectedStageNames = defaultStages.map((stage) => stage.name).join(', ');

async function importContactRows(parsed: ParsedCsv): Promise<ImportResult> {
  const result = initResult();
  const index = buildIndex(parsed.headers);

  for (let i = 0; i < parsed.rows.length; i += 1) {
    const row = parsed.rows[i];
    if (!row.some((cell) => cell.trim())) continue;
    const rowNumber = i + 2;

    const email = getField(row, index, ['email', 'emailaddress']).toLowerCase();
    let firstName = getField(row, index, ['firstname', 'first', 'givenname']);
    const lastName = getField(row, index, ['lastname', 'last', 'surname']);
    const phone = getField(row, index, ['phone', 'phonenumber']);
    const company = getField(row, index, ['company', 'organization', 'companyname']);
    const tagsRaw = getField(row, index, ['tags', 'labels', 'taglist']);

    if (!firstName) {
      if (email) {
        firstName = email.split('@')[0] ?? '';
      }
      if (!firstName) {
        result.failed += 1;
        result.errors.push(`Row ${rowNumber}: Missing first name or email.`);
        continue;
      }
    }

    if (email && !email.includes('@')) {
      result.failed += 1;
      result.errors.push(`Row ${rowNumber}: Email address \"${email}\" is invalid.`);
      continue;
    }

    const payload = {
      firstName,
      lastName: lastName || undefined,
      email: email || undefined,
      phone: phone || undefined,
      company: company || undefined,
      tags: tagsRaw
        ? tagsRaw
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean)
        : undefined,
    } as const;

    try {
      await createContact(payload);
      result.success += 1;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create contact';
      result.failed += 1;
      result.errors.push(`Row ${rowNumber}: ${message}`);
    }
  }

  return result;
}

async function importLeadsRows(parsed: ParsedCsv): Promise<ImportResult> {
  const result = initResult();
  const index = buildIndex(parsed.headers);
  const existingContacts = await fetchContacts();
  const contactCache = new Map<string, Contact>();
  existingContacts.forEach((contact) => {
    if (contact.email) {
      contactCache.set(contact.email.toLowerCase(), contact);
    }
  });

  for (let i = 0; i < parsed.rows.length; i += 1) {
    const row = parsed.rows[i];
    if (!row.some((cell) => cell.trim())) continue;
    const rowNumber = i + 2;

    const email = getField(row, index, ['contactemail', 'email']).toLowerCase();
    if (!email) {
      result.failed += 1;
      result.errors.push(`Row ${rowNumber}: contactEmail is required.`);
      continue;
    }

    if (!email.includes('@')) {
      result.failed += 1;
      result.errors.push(`Row ${rowNumber}: contactEmail \"${email}\" is invalid.`);
      continue;
    }

    let contact = contactCache.get(email);
    if (!contact) {
      const firstNameRaw = getField(row, index, ['firstname', 'contactfirstname', 'first']);
      const lastNameRaw = getField(row, index, ['lastname', 'contactlastname', 'last']);
      const phone = getField(row, index, ['phone', 'phonenumber']);
      const company = getField(row, index, ['company', 'organization']);

      const firstName = firstNameRaw || email.split('@')[0] || 'Imported';

      try {
        const created = await createContact({
          firstName,
          lastName: lastNameRaw || undefined,
          email,
          phone: phone || undefined,
          company: company || undefined,
        });
        contact = {
          id: created.id,
          first_name: firstName,
          last_name: lastNameRaw || null,
          email,
          phone: phone || null,
          company: company || null,
          tags: null,
          created_at: Date.now() / 1000,
          deal_count: 0,
        } as Contact;
        contactCache.set(email, contact);
        result.createdContacts += 1;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to create contact';
        result.failed += 1;
        result.errors.push(`Row ${rowNumber}: ${message}`);
        continue;
      }
    }

    const status = getField(row, index, ['status']);
    const source = getField(row, index, ['source']);
    const notes = getField(row, index, ['notes']);
    const scoreValue = getField(row, index, ['score']);
    const score = parseNumber(scoreValue);

    try {
      await createLead({
        contactId: contact.id,
        status: status || undefined,
        source: source || undefined,
        score: score !== undefined ? Math.round(score) : undefined,
        notes: notes || undefined,
      });
      result.success += 1;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create lead';
      result.failed += 1;
      result.errors.push(`Row ${rowNumber}: ${message}`);
    }
  }

  return result;
}

async function importDealsRows(parsed: ParsedCsv): Promise<ImportResult> {
  const result = initResult();
  const index = buildIndex(parsed.headers);
  const existingContacts = await fetchContacts();
  const contactCache = new Map<string, Contact>();
  existingContacts.forEach((contact) => {
    if (contact.email) {
      contactCache.set(contact.email.toLowerCase(), contact);
    }
  });

  for (let i = 0; i < parsed.rows.length; i += 1) {
    const row = parsed.rows[i];
    if (!row.some((cell) => cell.trim())) continue;
    const rowNumber = i + 2;

    const name = getField(row, index, ['dealname', 'name']);
    if (!name) {
      result.failed += 1;
      result.errors.push(`Row ${rowNumber}: Deal name is required.`);
      continue;
    }

    const email = getField(row, index, ['contactemail', 'email']).toLowerCase();
    if (!email) {
      result.failed += 1;
      result.errors.push(`Row ${rowNumber}: contactEmail is required.`);
      continue;
    }

    let contact = contactCache.get(email);
    if (!contact) {
      const firstName = getField(row, index, ['firstname', 'contactfirstname', 'first']) || email.split('@')[0] || 'Imported';
      const lastName = getField(row, index, ['lastname', 'contactlastname', 'last']);
      try {
        const created = await createContact({
          firstName,
          lastName: lastName || undefined,
          email,
        });
        contact = {
          id: created.id,
          first_name: firstName,
          last_name: lastName || null,
          email,
          phone: null,
          company: null,
          tags: null,
          created_at: Date.now() / 1000,
          deal_count: 0,
        } as Contact;
        contactCache.set(email, contact);
        result.createdContacts += 1;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to create contact';
        result.failed += 1;
        result.errors.push(`Row ${rowNumber}: ${message}`);
        continue;
      }
    }

    const stageInput = getField(row, index, ['stage', 'stageid', 'stagename']);
    const normalizedStage = stageInput.toLowerCase().trim();
    const matchesStage = defaultStages.some(
      (stage) =>
        stage.id.toLowerCase() === normalizedStage || stage.name.toLowerCase() === normalizedStage,
    );
    const stageId = resolveStageId(stageInput);
    if (!stageInput) {
      result.warnings.push(`Row ${rowNumber}: Stage missing, defaulted to ${defaultStages[0].name}.`);
    } else if (!matchesStage) {
      result.warnings.push(`Row ${rowNumber}: Stage \"${stageInput}\" not recognized, defaulted to ${defaultStages[0].name}.`);
    }

    const value = parseNumber(getField(row, index, ['value', 'amount'])) ?? 0;
    const probability = parseNumber(getField(row, index, ['probability', 'probabilitypercent']));
    const closeDate = parseDateToSeconds(getField(row, index, ['closedate', 'close', 'closeat']));

    try {
      await createDeal({
        contactId: contact.id,
        name,
        stageId,
        value,
        probability: probability !== undefined ? Math.round(probability) : undefined,
        closeDate: closeDate ?? undefined,
      });
      result.success += 1;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create deal';
      result.failed += 1;
      result.errors.push(`Row ${rowNumber}: ${message}`);
    }
  }

  return result;
}

async function runImport(type: ImportType, parsed: ParsedCsv): Promise<ImportResult> {
  if (type === 'contacts') return importContactRows(parsed);
  if (type === 'leads') return importLeadsRows(parsed);
  return importDealsRows(parsed);
}

export function ImportPage() {
  const [searchParams] = useSearchParams();
  const [type, setType] = useState<ImportType>('contacts');
  const [fileName, setFileName] = useState('');
  const [parsedCsv, setParsedCsv] = useState<ParsedCsv | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  const typeLabel = useMemo(
    () => typeOptions.find((option) => option.value === type)?.label ?? 'Contacts',
    [type],
  );

  useEffect(() => {
    const requestedType = searchParams.get('type');
    if (requestedType && ['contacts', 'leads', 'deals'].includes(requestedType)) {
      setType(requestedType as ImportType);
    }
  }, [searchParams]);

  const previewRows = useMemo(() => {
    if (!parsedCsv) return [] as string[][];
    return parsedCsv.rows.slice(0, 5);
  }, [parsedCsv]);

  async function handleFile(file: File) {
    setParseError(null);
    setResult(null);
    setFileName(file.name);
    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      setParsedCsv(parsed);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to parse CSV file';
      setParseError(message);
      setParsedCsv(null);
    }
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file) {
      void handleFile(file);
    }
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!parsedCsv) {
      setParseError('Select a CSV file before importing.');
      return;
    }
    setParseError(null);
    setIsImporting(true);
    setResult(null);
    try {
      const importResult = await runImport(type, parsedCsv);
      setResult(importResult);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Import failed';
      setParseError(message);
    } finally {
      setIsImporting(false);
    }
  }

  function resetState() {
    setFileName('');
    setParsedCsv(null);
    setParseError(null);
    setResult(null);
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Import CSV</h1>
          <p>{importDescriptions[type]}</p>
        </div>
        <div className="page-header-actions">
          <Link className="btn" to={`/${type}`}>
            Back to {typeLabel}
          </Link>
        </div>
      </div>

      <div className="card">
        <form className="form" onSubmit={onSubmit}>
          <label>
            Data type
            <select value={type} onChange={(event) => setType(event.target.value as ImportType)}>
              {typeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <div className="import-instructions">
            <p className="import-note">Include the following columns (order does not matter):</p>
            <ul className="import-columns">
              {columnGuides[type].map((column) => (
                <li key={column}>{column}</li>
              ))}
            </ul>
            {type === 'deals' && (
              <p className="import-note">Recognized stages: {expectedStageNames}.</p>
            )}
          </div>

          <label>
            CSV file
            <input type="file" accept=".csv,text/csv" onChange={handleFileChange} />
          </label>
          {fileName && <p className="import-file">Selected file: {fileName}</p>}

          {parseError && <p className="form-error">{parseError}</p>}

          {parsedCsv && (
            <div className="import-preview">
              <h3>Preview</h3>
              <div className="table-scroll">
                <table className="table">
                  <thead>
                    <tr>
                      {parsedCsv.headers.map((header) => (
                        <th key={header}>{header || 'Column'}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.length ? (
                      previewRows.map((row, idx) => (
                        <tr key={idx}>
                          {parsedCsv.headers.map((_, columnIdx) => (
                            <td key={columnIdx}>{row[columnIdx] ?? ''}</td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={parsedCsv.headers.length}>No rows detected.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button className="btn" type="button" onClick={resetState} disabled={isImporting}>
              Clear
            </button>
            <button className="btn btn--primary" type="submit" disabled={isImporting}>
              {isImporting ? 'Importing…' : 'Import data'}
            </button>
          </div>
        </form>
      </div>

      {result && (
        <div className="card import-summary">
          <h3>Import summary</h3>
          <ul className="import-summary__list">
            <li>
              <strong>{result.success}</strong> rows imported successfully
            </li>
            <li>
              <strong>{result.failed}</strong> rows failed
            </li>
            {result.createdContacts > 0 && (
              <li>
                <strong>{result.createdContacts}</strong> contacts created automatically
              </li>
            )}
          </ul>
          {result.warnings.length > 0 && (
            <div className="import-messages import-messages--warning">
              <strong>Warnings</strong>
              <ul>
                {result.warnings.map((warning, idx) => (
                  <li key={idx}>{warning}</li>
                ))}
              </ul>
            </div>
          )}
          {result.errors.length > 0 && (
            <div className="import-messages import-messages--error">
              <strong>Errors</strong>
              <ul>
                {result.errors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
